import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { UsersPartyPermissionsReadType } from '@portal/api/enums/users-party-permissions-read-type';
import { StorageService } from '@portal/shared/services/storage.service';
import { OrganizationType } from '@portal/api/enums/organization-type';

@Injectable({
    providedIn: 'root'
})
export class EventNavigationService {
    private _eventNavigationItems = new BehaviorSubject<FuseNavigationItem[]>([]);
    private _currentEventId: string | null = null;
    private _currentEventName: string | null = null;

    constructor(private readonly _storageService: StorageService) {
    }

    /**
     * Getter for event navigation items
     */
    get eventNavigationItems$(): Observable<FuseNavigationItem[]> {
        return this._eventNavigationItems.asObservable();
    }

    /**
     * Set event navigation items based on event data
     */
    setEventNavigation(eventId: string, eventName: string, eventData: any): void {
        this._currentEventId = eventId;
        this._currentEventName = eventName;

        // Return items directly without grouping
        const eventNavigationItems = this.buildEventTabItems(eventId, eventData);

        this._eventNavigationItems.next(eventNavigationItems);
    }

    /**
     * Clear event navigation items
     */
    clearEventNavigation(): void {
        this._currentEventId = null;
        this._currentEventName = null;
        this._eventNavigationItems.next([]);
    }

    /**
     * Get current event ID
     */
    getCurrentEventId(): string | null {
        return this._currentEventId;
    }

    /**
     * Get current event name
     */
    getCurrentEventName(): string | null {
        return this._currentEventName;
    }

    /**
     * Build event tab items based on event data
     */
    private buildEventTabItems(eventId: string, eventData: any): FuseNavigationItem[] {
        const tabItems: FuseNavigationItem[] = [];

        // Dashboard (always available)
        tabItems.push({
            id: `event-${eventId}-dashboard`,
            title: 'Painel',
            type: 'basic',
            link: `/party/events/${eventId}/dashboard`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Income/Receitas
        tabItems.push({
            id: `event-${eventId}-income`,
            title: 'Receitas',
            type: 'basic',
            link: `/party/events/${eventId}/income`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Expenses/Despesas
        tabItems.push({
            id: `event-${eventId}-expenses`,
            title: 'Despesas',
            type: 'basic',
            link: `/party/events/${eventId}/expenses`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Offers/Ofertas
        tabItems.push({
            id: `event-${eventId}-offers`,
            title: 'Ofertas',
            type: 'basic',
            link: `/party/events/${eventId}/offers`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Sponsors/Patrocinadores
        tabItems.push({
            id: `event-${eventId}-sponsors`,
            title: 'Patrocinadores',
            type: 'basic',
            link: `/party/events/${eventId}/sponsors`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Donations/Donativos
        tabItems.push({
            id: `event-${eventId}-donations`,
            title: 'Donativos',
            type: 'basic',
            link: `/party/events/${eventId}/donations`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Bar
        tabItems.push({
            id: `event-${eventId}-bar`,
            title: 'Postos de Venda',
            type: 'basic',
            link: `/party/events/${eventId}/bar`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Registrations/Inscrições
        tabItems.push({
            id: `event-${eventId}-registrations`,
            title: 'Inscrições',
            type: 'basic',
            link: `/party/events/${eventId}/registrations`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        tabItems.push({
            id: `event-${eventId}-inventory`,
            title: 'Inventário',
            type: 'basic',
            link: `/party/events/${eventId}/inventory`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        // Roadmap/Guião
        tabItems.push({
            id: `event-${eventId}-roadmap`,
            title: 'Guião / Tarefas',
            type: 'basic',
            link: `/party/events/${eventId}/roadmap`,
            permissions: [UsersPartyPermissionsReadType.EventsRead]
        });

        return tabItems;
    }
}
